import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// 导入语言包
import en from './locales/en';
import zh from './locales/zh';

// 支持的语言列表
export const SUPPORTED_LANGUAGES = {
  'en-US': 'English',
  'zh-CN': '简体中文'
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// 默认语言
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en-US';

// 本地存储的key
const LANGUAGE_STORAGE_KEY = 'i18n_language';
const MANUAL_SWITCH_FLAG_KEY = 'i18n_manual_switch';

// 语言代码映射（向后兼容）
const LEGACY_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  'en': 'en-US',
  'zh': 'zh-CN'
};

// 获取存储的语言设置
export const getStoredLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      // 检查是否是新格式
      if (stored in SUPPORTED_LANGUAGES) {
        return stored as SupportedLanguage;
      }
      // 检查是否是旧格式，进行转换
      if (stored in LEGACY_LANGUAGE_MAP) {
        const newLang = LEGACY_LANGUAGE_MAP[stored];
        // 更新到新格式
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
        return newLang;
      }
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  return DEFAULT_LANGUAGE;
};

// 获取存储的语言设置（如果没有则返回 null）
export const getStoredLanguageOrNull = (): SupportedLanguage | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      // 检查是否是新格式
      if (stored in SUPPORTED_LANGUAGES) {
        return stored as SupportedLanguage;
      }
      // 检查是否是旧格式，进行转换
      if (stored in LEGACY_LANGUAGE_MAP) {
        const newLang = LEGACY_LANGUAGE_MAP[stored];
        // 更新到新格式
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
        return newLang;
      }
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  return null;
};

// 设置语言到本地存储
export const setStoredLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
};

// 设置手动切换标识
export const setManualSwitchFlag = (): void => {
  try {
    localStorage.setItem(MANUAL_SWITCH_FLAG_KEY, 'true');
  } catch (error) {
    console.warn('Failed to save manual switch flag:', error);
  }
};

// 检查是否是手动切换
export const isManualSwitch = (): boolean => {
  try {
    return localStorage.getItem(MANUAL_SWITCH_FLAG_KEY) === 'true';
  } catch (error) {
    console.warn('Failed to read manual switch flag:', error);
    return false;
  }
};

// 清除手动切换标识
export const clearManualSwitchFlag = (): void => {
  try {
    localStorage.removeItem(MANUAL_SWITCH_FLAG_KEY);
  } catch (error) {
    console.warn('Failed to clear manual switch flag:', error);
  }
};

// 初始化i18n（先设置一个默认语言，然后等待 useLanguageInitialization hook 来管理）
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'en-US': {
        translation: en
      },
      'zh-CN': {
        translation: zh
      }
    },
    lng: getStoredLanguage(), // 先使用存储的语言作为初始值
    fallbackLng: DEFAULT_LANGUAGE,
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false, // React已经安全地转义了
      prefix: '{',
      suffix: '}'
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    }
  });

// 导出初始化语言的函数
export const initializeLanguage = (language: SupportedLanguage) => {
  if (language && i18n.language !== language) {
    i18n.changeLanguage(language);
    setStoredLanguage(language);
  }
};

export default i18n;
